import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { userPreferencesApi } from '../lib/api'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  organizationId: string
  role?: string
  preferences?: {
    preferredApplication?: string;
    theme?: string;
    language?: string;
    [key: string]: any;
  }
}

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  organizationName?: string
}

export const useAuth = () => {
  const queryClient = useQueryClient()
  const [isInitialized, setIsInitialized] = useState(false)

  const { data: user, isLoading } = useQuery<User>({
    queryKey: ['user'],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No token found')
      }
      const response = await api.get('/auth/me')
      return response.data.user
    },
    enabled: !!localStorage.getItem('token'),
    retry: false,
  })

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await api.post('/auth/login', credentials)
      return response.data
    },
    onSuccess: (data) => {
      localStorage.setItem('token', data.access_token)
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      const response = await api.post('/auth/register', data)
      return response.data
    },
    onSuccess: (data) => {
      localStorage.setItem('token', data.access_token)
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  const updatePreferencesMutation = useMutation({
    mutationFn: async (preferences: {
      preferredApplication?: string;
      theme?: string;
      language?: string;
      [key: string]: any;
    }) => {
      const response = await userPreferencesApi.updatePreferences(preferences)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('preferredApplication') // Clear local preference backup
    queryClient.clear()
  }

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  return {
    user,
    isLoading: !isInitialized || isLoading,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    updatePreferences: updatePreferencesMutation.mutate,
    logout,
    isLoginLoading: loginMutation.isPending,
    isRegisterLoading: registerMutation.isPending,
    isUpdatePreferencesLoading: updatePreferencesMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    updatePreferencesError: updatePreferencesMutation.error,
  }
}